/**
 * API сервис для работы с CRD файлами
 */

import config from '../config/apiConfig';
import { debugInfo, debugError } from '../utils/debug';

const API_URL = config.functions?.crdParser?.url || process.env.REACT_APP_CRD_PARSER_URL;

/**
 * Парсит все .crd файлы пользователя
 * @param {string} userEmail - Email пользователя
 * @param {string} format - Формат вывода (json, csv, tsv)
 * @param {boolean} force - Принудительный перепарсинг (игнорировать кеш)
 * @returns {Promise<Object>} - Результат парсинга
 */
export async function parseAllCrdFiles(userEmail, format = 'json', force = false) {
  debugInfo('CrdApi', 'Parsing all CRD files', { userEmail, format, force });
  
  try {
    if (!API_URL) {
      throw new Error('CRD Parser function URL not configured');
    }

    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userEmail,
        parseAll: true,
        format,
        force,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to parse CRD files');
    }

    debugInfo('CrdApi', 'CRD files parsed successfully', result);
    return result;
    
  } catch (error) {
    debugError('CrdApi', 'Error parsing CRD files', error);
    throw error;
  }
}

/**
 * Парсит конкретный .crd файл
 * @param {string} userEmail - Email пользователя
 * @param {string} fileKey - Ключ файла в S3
 * @param {string} format - Формат вывода (json, csv, tsv)
 * @param {boolean} force - Принудительный перепарсинг (игнорировать кеш)
 * @returns {Promise<Object>} - Результат парсинга
 */
export async function parseCrdFile(userEmail, fileKey, format = 'json', force = false) {
  debugInfo('CrdApi', 'Parsing CRD file', { userEmail, fileKey, format, force });
  
  try {
    if (!API_URL) {
      throw new Error('CRD Parser function URL not configured');
    }

    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userEmail,
        fileKey,
        format,
        force,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to parse CRD file');
    }

    debugInfo('CrdApi', 'CRD file parsed successfully', result);
    return result;
    
  } catch (error) {
    debugError('CrdApi', 'Error parsing CRD file', error);
    throw error;
  }
}

/**
 * Парсит CRD файлы с продвинутым парсером
 * @param {string} userEmail - Email пользователя
 * @param {boolean} parseAll - Парсить все файлы
 * @param {string} fileKey - Ключ конкретного файла (если не parseAll)
 * @param {string} format - Формат вывода (json, csv, tsv)
 * @returns {Promise<Object>} - Результат парсинга
 */
export async function parseAdvancedCrd(userEmail, parseAll = true, fileKey = null, format = 'json') {
  debugInfo('CrdApi', 'Parsing CRD with advanced parser', { 
    userEmail, 
    parseAll, 
    fileKey, 
    format 
  });
  
  try {
    if (!API_URL) {
      throw new Error('CRD Parser function URL not configured');
    }

    const body = {
      userEmail,
      format,
      useAdvanced: true,
    };

    if (parseAll) {
      body.parseAll = true;
    } else if (fileKey) {
      body.fileKey = fileKey;
    } else {
      throw new Error('Either parseAll or fileKey must be specified');
    }

    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to parse CRD files');
    }

    debugInfo('CrdApi', 'Advanced CRD parsing completed', result);
    return result;
    
  } catch (error) {
    debugError('CrdApi', 'Error in advanced CRD parsing', error);
    throw error;
  }
}

/**
 * Получает полные данные документа из распарсенных CRD файлов
 * @param {string} userEmail - Email пользователя
 * @param {string} tableName - Название таблицы (например, 'СЧЕТФАКТ')
 * @param {string|number} documentId - ID документа (UKEY)
 * @param {string} yearMonth - Год-месяц в формате YYYY-MM (опционально)
 * @returns {Promise<Object>} - Результат с полными данными документа
 */
export async function getCrdDocument(userEmail, tableName, documentId, yearMonth = null) {
  debugInfo('CrdApi', 'Getting CRD document', { userEmail, tableName, documentId, yearMonth });
  
  try {
    if (!API_URL) {
      throw new Error('CRD Parser function URL not configured');
    }

    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userEmail,
        action: 'getCrdDocument',
        tableName,
        documentId: String(documentId), // Преобразуем в строку на всякий случай
        yearMonth,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to get CRD document');
    }

    debugInfo('CrdApi', 'CRD document retrieved successfully', result);
    return result;
    
  } catch (error) {
    debugError('CrdApi', 'Error getting CRD document', error);
    throw error;
  }
}

export default {
  parseAllCrdFiles,
  parseCrdFile,
  parseAdvancedCrd,
  getCrdDocument,
};
